import React, { useState, useEffect, useRef } from 'react';
import TripCard from './TripCard';
import TripForm from './TripForm';
import './Trips.css';

const Trips = ({ user }) => {
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [filterAction, setFilterAction] = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTrips();
    fetchVehicles();
  }, []);

  const fetchTrips = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/trips.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setTrips(data.data);
      } else {
        setError('Failed to fetch trips');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/vehicles.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setVehicles(data.data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleAddTrip = () => {
    setShowForm(true);
  };

  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResults(null);
    setImportFile(null);
    setError('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
      setError('');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImportLoading(true);
    setError('');
    
    const formData = new FormData();
    formData.append('excel_file', importFile);
    formData.append('action', 'import');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/trips.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        setImportResults(data);
        setSuccess(`Import completed: ${data.summary.successful} successful, ${data.summary.failed} failed, ${data.summary.invalid_vehicles} invalid vehicles`);
        setTimeout(() => setSuccess(''), 5000);
        fetchTrips();
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (error) {
      setError('Error during import');
      console.error('Import error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['VehicleNumber', 'Action', 'RTKM', 'HSD', 'AdvanceExp', 'Destination', 'Timestamp', 'Notes'],
      ['KA01AB1234', 'BOARD', '100', '42.5', '1500', 'Warehouse', '2024-01-15 09:30:00', 'Loaded with electronics'],
      ['MH03CD5678', 'BOARD', '50', '34', '100', 'Port', '2024-01-16 10:00:00', 'Export shipment']
    ];
    
    let csvContent = "data:text/csv;charset=utf-8,";
    templateData.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(",") + "\r\n";
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "trip_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFormClose = (added) => {
    setShowForm(false);
    if (added) {
      setSuccess('Trip added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchTrips();
    }
  };

  const filteredTrips = trips.filter(trip => {
    const matchesAction = filterAction === 'all' || trip.action === filterAction;
    const matchesVehicle = filterVehicle === 'all' || trip.vehicle_id == filterVehicle;
    const matchesDate = !filterDate || new Date(trip.action_time).toDateString() === new Date(filterDate).toDateString();
    
    return matchesAction && matchesVehicle && matchesDate;
  });

  const stats = {
    total: trips.length,
    boarded: trips.filter(t => t.action === 'BOARD').length,
    unboarded: trips.filter(t => t.action === 'UNBOARD').length,
    today: trips.filter(t => new Date(t.action_time).toDateString() === new Date().toDateString()).length
  };

  return (
    <div className="trips-container">
      <div className="trips-header">
        <div>
          <h1>Trip Management</h1>
          <p className="page-subtitle">Track all boarding and unboarding operations</p>
        </div>
        <div className="header-actions">
          {user?.role === 'admin' && (
            <button className="btn-secondary" onClick={handleImportClick}>
              <span>📤</span> Import Excel
            </button>
          )}
          <button className="btn-primary" onClick={handleAddTrip}>
            <span>+</span> Add Trip
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <span>❌</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span> {success}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card total">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <h3>Total Active Trips</h3>
            <p>{stats.total}</p>
          </div>
        </div>
        <div className="stat-card today">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <h3>Today's Trips</h3>
            <p>{stats.today}</p>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Action Type</label>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filterAction === 'all' ? 'active' : ''}`}
              onClick={() => setFilterAction('all')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${filterAction === 'BOARD' ? 'active' : ''}`}
              onClick={() => setFilterAction('BOARD')}
            >
              BOARD
            </button>
            <button 
              className={`filter-btn ${filterAction === 'UNBOARD' ? 'active' : ''}`}
              onClick={() => setFilterAction('UNBOARD')}
            >
              UNBOARD
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Vehicle</label>
          <select 
            value={filterVehicle} 
            onChange={(e) => setFilterVehicle(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Vehicles</option>
            {vehicles.map(vehicle => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.vehicle_number} - {vehicle.driver_name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date</label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="filter-date"
          />
        </div>

        <button 
          className="btn-secondary"
          onClick={() => {
            setFilterAction('all');
            setFilterVehicle('all');
            setFilterDate('');
          }}
        >
          Clear Filters
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading trips...</p>
        </div>
      ) : filteredTrips.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🗺️</div>
          <h3>No trips found</h3>
          <p>{filterAction !== 'all' || filterVehicle !== 'all' || filterDate ? 'Try changing your filters' : 'Add your first trip to get started'}</p>
          {!filterAction && !filterVehicle && !filterDate && (
            <div className="empty-state-actions">
              <button className="btn-primary" onClick={handleAddTrip}>
                Add First Trip
              </button>
              {user?.role === 'admin' && (
                <button className="btn-secondary" onClick={handleImportClick}>
                  Import from Excel
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="trips-timeline">
          <h3>Trip Timeline</h3>
          <div className="timeline">
            {filteredTrips.map(trip => (
              <TripCard 
                key={trip.id} 
                trip={trip} 
                vehicle={vehicles.find(v => v.id == trip.vehicle_id)}
              />
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <TripForm 
              vehicles={vehicles} 
              onClose={handleFormClose} 
            />
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal import-modal">
            <div className="modal-header">
              <h2>Import Trips from Excel/CSV</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                  setImportFile(null);
                }}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-content">
              
              
              <div className="file-upload-section">
                <h3>📁 Upload File</h3>
                <div 
                  className={`file-drop-area ${importFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />
                  
                  {importFile ? (
                    <div className="file-selected">
                      <span className="file-icon">📄</span>
                      <div className="file-info">
                        <strong>{importFile.name}</strong>
                        <small>{(importFile.size / 1024).toFixed(2)} KB</small>
                      </div>
                      <button 
                        className="remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImportFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="upload-icon">📤</span>
                      <p>Click to select or drag & drop Excel/CSV file</p>
                      <p className="file-types">Supported: .xlsx, .xls, .csv</p>
                    </>
                  )}
                </div>
              </div>
              
              {importResults && (
                <div className="import-results">
                  <h3>📊 Import Results</h3>
                  <div className="results-summary">
                    <div className="result-stat success">
                      <span>✅</span>
                      <div>
                        <h4>Successful</h4>
                        <p>{importResults.summary.successful}</p>
                      </div>
                    </div>
                    <div className="result-stat failed">
                      <span>❌</span>
                      <div>
                        <h4>Failed</h4>
                        <p>{importResults.summary.failed}</p>
                      </div>
                    </div>
                    <div className="result-stat invalid">
                      <span>⚠️</span>
                      <div>
                        <h4>Invalid Vehicles</h4>
                        <p>{importResults.summary.invalid_vehicles}</p>
                      </div>
                    </div>
                  </div>
                  
                  {importResults.details && importResults.details.some(d => d.status === 'error') && (
                    <div className="error-details">
                      <h4>Failed Rows:</h4>
                      <div className="error-list">
                        {importResults.details
                          .filter(d => d.status === 'error')
                          .map((detail, index) => (
                            <div key={index} className="error-item">
                              <span>Row {detail.row}:</span> {detail.vehicle_number} - {detail.message}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {importResults.details && importResults.details.some(d => d.status === 'success') && (
                    <div className="success-details">
                      <h4>Recent Successful Imports:</h4>
                      <div className="success-list">
                        {importResults.details
                          .filter(d => d.status === 'success')
                          .slice(0, 5)
                          .map((detail, index) => (
                            <div key={index} className="success-item">
                              <span>Row {detail.row}:</span> {detail.vehicle_number} - {detail.action}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="import-instructions">
                <h3>📋 Instructions:</h3>
                <ol>
                  <li>Download the template below to see the required format</li>
                  <li>Fill in your trip data with columns: <strong>VehicleNumber, Action (BOARD/UNBOARD),Rtkm, Hsd, Advance Exp, Destination, Timestamp (optional), Notes</strong></li>
                  <li>Timestamp format: YYYY-MM-DD HH:MM:SS (if empty, current time will be used)</li>
                  <li>Upload your Excel (.xlsx, .xls) or CSV file</li>
                  <li>Review and confirm import</li>
                </ol>
                
                <div className="template-section">
                  <button className="btn-secondary" onClick={downloadTemplate}>
                    📥 Download Template
                  </button>
                  <p className="template-note">Use the template to ensure proper formatting</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                  setImportFile(null);
                }}
                disabled={importLoading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleImport}
                disabled={!importFile || importLoading}
              >
                {importLoading ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Importing...
                  </>
                ) : (
                  'Import Trips'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips;